import { useEffect, useState } from "react";
import { useAuth } from "../authContext";
import PartnerService from "../services/manager/partnerService";
import OrderService from "../services/manager/orderService";
import dayjs from "dayjs";
import { Autocomplete, Box, Button, Container, CssBaseline, Grid, Pagination, Snackbar, SnackbarContent, TextField, Typography } from "@mui/material";
import AdminNavigation from "./adminNavigation";
import { btnStyleContrast, lightergrayHex, subHeaderStyleContrast, textSmallStyleContrast, headerStyleContrast, textStyleContrast } from "../helper/style";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { NumericFormat } from "react-number-format";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PartnerTransactionPDF from "./partnerTransactionPDF";

const PartnerTransaction = () => {

  const [ShowSuccess, setShowSuccess] = useState(false);
  const [MsgInsert, setMsgInsert] = useState("");
  const [msgError, setMsgError] = useState("");
  const [showError, setShowError] = useState(false);

  const [lastUpdate, setLastUpdate] = useState(null);
  const [generateInvoiceDate, setGenerateInvoiceDate] = useState(new Date());
  const [income, setIncome] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalItem, setTotalItem] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [fileName, setFileName] = useState("");

  const { auth, logout } = useAuth();
  const getId = auth.user ? auth.user.id : "";
  const getUsername = auth.user ? auth.user.username : "";
  const getName = auth.user ? auth.user.name : "";
  const hakAkses = auth.user ? auth.user.accessRight : null;

  const [partnerId, setPartnerId] = useState(hakAkses?.position === "Mitra" ? { id: getId, username: getUsername, name: getName } : null);
  const [partners, setPartners] = useState([]);

  const [printData, setPrintData] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(false);

  const handleShowError = (message) => {
    setMsgError(message);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
    }, 5000);
  }

  const handleShowSuccess = (message) => {
    setMsgInsert(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 5000);
  }

  const getAllPartner = async () => {
    try {
      const response = await PartnerService.getListPartnerAll();
      if (response) {
        const data = response.data;
        setPartners(data.partnerList);
      }
    } catch (error) {
      console.error(error);
      handleShowError("Error while fetching list partner");
    }
  }

  const fetchTransaction = async () => {
    try {
      const response = await OrderService.getListTransactions(
        page, 
        perPage, 
        partnerId?.id,
        dateFrom ? dateFrom.format("YYYY-MM-DD") : null,
        dateTo ? dateTo.format("YYYY-MM-DD") : null
      );
      if (response) {
        const data = response.data;
        setTransactions(data.transactionList);
        setTotalItem(data.totalItems);
        setTotalPage(data.totalPage);
        setIncome(data.totalIncome || 0);
        setLastUpdate(dayjs(new Date()));

        setPrintData(null);
      }
    } catch (error) {
      handleShowError("Gagal mendapatkan pesanan")
    }
  };

  const fetchPrintData = async () => {
    try {
      setLoadingPrint(true);
      const response = await OrderService.getListTransactionsPrint(
        partnerId?.id,
        dateFrom ? dateFrom.format("YYYY-MM-DD") : null,
        dateTo ? dateTo.format("YYYY-MM-DD") : null
      );
      if (response) {
        const data = response.data;
        setGenerateInvoiceDate(new Date());
        setPrintData(data);
      }
    } catch (error) {
      handleShowError("Gagal mendapatkan pesanan")
    } finally {
      setLoadingPrint(false);
    }
  };

  useEffect(() => {
    getAllPartner();
  }, []);

  useEffect(() => {
    if (partnerId) {
      setPage(1)
      fetchTransaction();
    }
  }, [partnerId, dateFrom, dateTo])

   useEffect(() => {
    if (partnerId) {
      fetchTransaction();
      window.scrollTo(0, 0);
    }
  }, [page])

  useEffect(() => {
    if (printData) {
      setFileName(
        `${dayjs(generateInvoiceDate).format("YYYYMMDD")}_${printData?.partner?.name?.toUpperCase()?.replaceAll(" ", "-")}_INVOICE.pdf`
      );
    }
  }, [generateInvoiceDate, printData]);

  return (
    <Box>
      <CssBaseline />
      <AdminNavigation />
      <Container component="main" maxWidth="xl">
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={ShowSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
        >
          <SnackbarContent
            sx={{ backgroundColor: "#19B600" }} 
            message={MsgInsert}/>
        </Snackbar>
  
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={showError}
          autoHideDuration={3000}
          onClose={() => setShowError(false)}
        >
          <SnackbarContent 
            sx={{ backgroundColor: "#E50B0B" }} 
            message={msgError}/>
        </Snackbar>

        <Grid container spacing={1} alignItems="center" marginBottom={2}>
          <Grid item xs={12} md={6}>
            <Typography sx={{ ...headerStyleContrast }}>
              Transaksi
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography>Tanggal Awal</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-sg">
              <DatePicker
                sx={{ width: "100%" }}
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography>Tanggal Akhir</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-sg">
              <DatePicker
                sx={{ width: "100%" }}
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </LocalizationProvider>
          </Grid>
          {
            hakAkses?.position !== "Mitra" && (
              <Grid item xs={12} sm={4} md={2}>
                <Typography>Mitra</Typography>
                <Autocomplete
                  options={partners}
                  getOptionLabel={(option) => option.name}
                  value={partnerId ? { ...partnerId } : null}
                  isOptionEqualToValue={(option, value) => {
                    return option.id === value.id;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth />
                  )}
                  onChange={(event, newValue) =>
                    setPartnerId(newValue)
                  }
                />
              </Grid>
            )
          }
        </Grid>

        <Box 
          sx={{ 
            width: "100%",  background: "white", 
            padding: "16px", borderRadius: "16px", 
            marginTop: "20px", boxShadow: "2px 2px 10px rgba(0, 0, 0, .2)" 
          }}
        >
          <Typography sx={{ ...textStyleContrast, color: lightergrayHex }} display="flex" alignItems="center" gap="10px">
            <CalendarTodayOutlinedIcon /> <span>Last Update: {lastUpdate?.format("DD/MM/YY hh:mm A") || '-'}</span>
          </Typography>
          <Grid container marginTop={2}>
            <Grid item xs={8} md={10}>
              <Typography sx={{ ...textSmallStyleContrast, color: lightergrayHex }}>
                Total Pendapatan
              </Typography>
              <Typography sx={{ ...subHeaderStyleContrast }}>
                <NumericFormat
                  value={income}
                  displayType={"text"}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix={"Rp "}
                />
              </Typography>
            </Grid>
            <Grid item xs={4} md={2} display="flex" alignItems="end">
              {
                printData == null ? (
                  <Button
                    sx={{ ...btnStyleContrast, borderRadius: "50px", width: "100%" }} 
                    disabled={loadingPrint}
                    onClick={fetchPrintData} 
                    startIcon={<FileDownloadOutlinedIcon sx={{ color: "white" }} />}
                  >
                    { loadingPrint ? "Mohon Menunggu..." : "Generate Invoice" }
                  </Button>
                ) : (
                  <PDFDownloadLink
                    style={{ width: "100%" }}
                    document={
                      <PartnerTransactionPDF 
                        transactionData={printData} 
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                      />
                    }
                    fileName={fileName}
                  >
                    {({loading}) => (
                      <Button
                        sx={{ ...btnStyleContrast, borderRadius: "50px", width: "100%" }} 
                        disabled={loading}
                        startIcon={<FileDownloadOutlinedIcon sx={{ color: "white" }} />}
                      >
                        {loading ? "Mohon Menunggu..." : "Cetak Invoice"}
                      </Button>
                    )}
                  </PDFDownloadLink>
                )
              }
            </Grid>
          </Grid>
        </Box>

        <Box 
          sx={{ 
            width: "100%",  background: "white", 
            padding: "16px", borderRadius: "16px", 
            marginY: "20px", boxShadow: "2px 2px 10px rgba(0, 0, 0, .2)" 
          }}
        >
          <Typography sx={{ ...textStyleContrast, fontWeight: "bold" }} marginBottom={2}>
            Riwayat Transaksi
          </Typography>
          {
            transactions.map(transaction => (
              <Grid container key={transaction.id} sx={{ marginBottom: "20px" }}>
                <Grid item xs={3} md={3}>
                  <Typography sx={{ ...textStyleContrast, color: lightergrayHex }}>
                    {dayjs(transaction.transactionDate).format("DD MMMM YYYY, HH:mm")} 
                  </Typography>
                </Grid>
                <Grid item xs={6} md={6}>
                  <Typography sx={{ ...textStyleContrast }}>
                    {transaction.type === 'O' ? 'Penjualan Produk' : 'Penarikan Dana'}
                  </Typography>
                </Grid>
                <Grid item xs={3} md={3}>
                  <Typography sx={{ ...textStyleContrast }}>
                    <NumericFormat
                      allowNegative={false}
                      value={transaction.totalTransaction} 
                      displayType={"text"} 
                      thousandSeparator="." 
                      decimalSeparator=","
                      prefix={`${transaction.totalTransaction < 0 ? '-' : '+'} Rp `} />
                  </Typography>
                </Grid>
              </Grid>
            ))
          }

          <Box display="flex" justifyContent="center" marginBottom={1}>
            <Pagination sx={{ 
                '& .MuiPaginationItem-root': {
                  color: 'black', 
                },
                '& .MuiPaginationItem-icon': {
                  color: 'black', 
                },
                '& .Mui-selected': {
                  backgroundColor: 'black !important', 
                  color: 'white', 
                },
              }} 
              size="small"
              count={totalPage} 
              page={page}
              onChange={(_event, page) => setPage(page)} />
          </Box>  
        </Box>
      </Container>
    </Box>
  )
} 

export default PartnerTransaction;